/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, string, func,
} from 'prop-types';
import { useDispatch } from 'react-redux';

import ComponentErrorBoundary from '../../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import PersonalizedHPCarouselBuilder from '../PersonalizedHomePageCarouselBuilder/PersonalizedHomePageCarouselBuilder';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import noop from '../../../../../helpers/noop';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import PersonalizedHPGridUI from '../PersonalizedHomePageGridUI/PersonalizedHomePageGridUI';

const DerivedReminderRecsBuilder = ({
    data, products_to_show, recommendationsType = 'derivedReminderRecs', setShowSkeleton,
}) => {
    const dispatch = useDispatch();
    const dynamicSalesforceData = data?.[`${recommendationsType}`];

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: dynamicSalesforceData,
        isFireImpression: dynamicSalesforceData?.campaign?.campaignResponses?.[0]?.experienceName === 'Control',
        page: { type: 'home' },
    });

    useEffect(() => {
        setShowSkeleton(false);
    }, []);

    const renderCarousel = (carouselTitle, products, productStyleConfig, containerPadding) => (
        <ComponentErrorBoundary function="PersonalizedHomePageSFContainer" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from Salesforce">
            <PersonalizedHPCarouselBuilder
                blockData={{
                    productsToShow: products_to_show || {},
                    title: carouselTitle,
                    titleMargin: productStyleConfig?.title_styling?.title_margin,
                    titleFontColor: productStyleConfig?.title_styling?.title_font_color?.color,
                    titleFontSize: productStyleConfig?.title_styling?.title_font_size,
                    titleFontStyle: productStyleConfig?.title_styling?.title_font_style,
                    titleFontFamily: productStyleConfig?.title_styling?.title_font_family,
                    alignTitle: productStyleConfig?.title_styling?.align_title,
                    padding: productStyleConfig?.title_styling?.padding,
                    containerPadding,
                }}
                productClickCallback={trackClickThrough}
                products={products}
                demandType="XS"
                productStyleConfig={productStyleConfig}
            />
        </ComponentErrorBoundary>
    );

    if (dynamicSalesforceData?.productRecs && dynamicSalesforceData?.productRecs.length > 0) {
        const recipientName = dynamicSalesforceData?.productRecsContent?.name;
        const title = dynamicSalesforceData?.productRecsContent?.main_title;
        // eslint-disable-next-line no-template-curly-in-string
        const titleSF = dynamicSalesforceData?.campaign?.campaignResponses?.[0]?.payload?.recipientRecTitle.replace('${recipientName}', recipientName) || dynamicSalesforceData?.campaign?.campaignResponses?.[0]?.payload?.fallbackRecipientRecTitle;
        const subTitle = dynamicSalesforceData?.campaign?.campaignResponses[0]?.payload?.recipientRecSubTitle || '';
        const isGridView = dynamicSalesforceData?.campaign?.campaignResponses?.[0]?.payload?.layoutDisplayType === 'GRID';
        const minimumProductsToShow = dynamicSalesforceData?.campaign?.campaignResponses?.[0]?.payload?.minimumProductsToShow || 2;
        const productRecs = dynamicSalesforceData?.productRecs;
        const productRecStylingConfig = dynamicSalesforceData?.productRecsContent?.personalized_product_card_reference?.length > 0 ? dynamicSalesforceData?.productRecsContent?.personalized_product_card_reference?.[0] : (dynamicSalesforceData?.productRecsContent || {});
        const carouselData = dynamicSalesforceData?.productRecsContent?.personalized_carousel;

        const trackImpression = () => dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            salesforceResponse: dynamicSalesforceData,
            page: { type: 'home' },
            eventSubType: 'campaign-tracking',
            displayType: '',
            minimumProductsToShow,
        }));

        trackImpression();

        if (isGridView) {
            return (
                <ComponentErrorBoundary function="PersonalizedHPGridUI" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from Salesforce">
                    <PersonalizedHPGridUI
                        sfData={data}
                        cmsData={{
                            title: titleSF, subTitle, min_products_to_show: minimumProductsToShow, padding: carouselData?.padding,
                        }}
                        isNewDesign={false}
                        handleClickCallback={trackClickThrough}
                        productStyleConfig={null}
                        productRecStyleConfig={{ ...productRecStylingConfig, main_title: title || titleSF }}
                        recommendationsType={recommendationsType}
                    />
                </ComponentErrorBoundary>
            );
        }
        return renderCarousel(title || titleSF, productRecs, productRecStylingConfig, carouselData?.padding);
    }
    return null;
};

DerivedReminderRecsBuilder.propTypes = {
    data: object.isRequired,
    products_to_show: object,
    recommendationsType: string.isRequired,
    setShowSkeleton: func,
};

DerivedReminderRecsBuilder.defaultProps = {
    products_to_show: {},
    setShowSkeleton: noop,
};

export default DerivedReminderRecsBuilder;
